import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, RotateCcwIcon } from 'lucide-react';
import { useExperience } from '../../contexts/ExperienceContext';
import { flows } from '../../data/journeys';
import { SessionMemory } from './SessionMemory';
const stageNames: Record<string, string> = {
  'better-priority': 'Priority',
  'better-mirror': 'Reflection',
  'new-priority': 'What matters',
  'new-bridge': 'The idea',
  'existing-usage': 'Current use',
  'existing-competitor': 'Gaps',
  'existing-closer': 'Discovery',
  reveal: 'The idea',
  needs: 'The family',
  week: 'Your week',
  recommend: 'Your focus',
  family: 'Overview',
  deeper: 'Go deeper'
};
interface ExperienceChromeProps {
  showMemory: boolean;
}

/** Persistent, quiet frame: identity, where you are, and the session memory. */
export function ExperienceChrome({
  showMemory
}: ExperienceChromeProps) {
  const {
    route,
    stage,
    stageIndex,
    memory,
    accent,
    back,
    goTo,
    reset
  } = useExperience();
  const [open, setOpen] = useState(false);
  const onEntry = stage === 'entry';
  const sequence = route ? flows[route] : [];
  return <>
      <header className="pointer-events-none fixed inset-x-0 top-0 z-30 flex items-start justify-between px-6 py-5 sm:px-10 lg:px-16" data-id="element-7" data-mp="3h1xz7vunjp8t">
        <button type="button" onClick={reset} className="pointer-events-auto flex items-center gap-3 text-left" aria-label="Restart the Filtek Family experience" data-id="element-8" data-mp="37aa49kyvzq2l">
          <svg viewBox="0 0 24 24" className="h-7 w-7" aria-hidden="true" data-id="element-9" data-mp="2xbkt0b1hx3vb">
            <path d="M15.6 5.2c-2.4-1.9-6.1-1.1-7 1.7-.8 2.5 1.2 4 3.6 5.1 2.4 1.1 4.4 2.6 3.6 5.1-.9 2.8-4.6 3.6-7 1.7" fill="none" stroke="var(--brand)" strokeWidth="1.7" strokeLinecap="round" data-id="element-10" data-mp="2ta8x5ns13meo" />
          </svg>
          <span className="leading-none" data-id="element-11" data-mp="mjt1mjatvau6">
            <span className="label-mono block text-[10px] text-mute" data-id="element-12" data-mp="mkubwlk0rtau">Solventum</span>
            <span className="mt-1 block text-base font-light tracking-[-0.02em] text-enamel" data-id="element-13" data-mp="15o5ycs4ggmih">
              Filtek Family
            </span>
          </span>
        </button>

        {showMemory && <button type="button" onClick={() => setOpen(true)} className="pointer-events-auto flex items-center gap-3 rounded-full border border-enamel/15 bg-ink/40 px-4 py-2 backdrop-blur-md transition-colors hover:border-enamel/40" data-id="element-14" data-mp="woizacz77ssl">
            <span className="relative flex h-1.5 w-1.5" data-id="element-15" data-mp="1tdqnjsr7cg07">
              <span className="absolute inset-0 rounded-full" style={{
            backgroundColor: memory.length ? accent : 'var(--mute)'
          }} data-id="element-16" data-mp="1td7aapchyfsi" />
              {memory.length > 0 && <motion.span className="absolute inset-0 rounded-full" style={{
            backgroundColor: accent
          }} animate={{
            scale: [1, 3],
            opacity: [0.6, 0]
          }} transition={{
            duration: 2.2,
            repeat: Infinity,
            ease: 'easeOut'
          }} data-id="element-17" data-mp="1kf56k21dp91j" />}
            </span>
            <span className="label-mono text-[10px] text-enamel" data-id="element-18" data-mp="3sjvwasveruar">
              Session {memory.length > 0 && `· ${memory.length}`}
            </span>
          </button>}
      </header>

      <AnimatePresence data-id="element-19" data-mp="5fzli1wecvkq">
        {!onEntry && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.5
      }} className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-4 px-6 py-5 sm:px-10 lg:px-16" data-id="element-20" data-mp="5e4vi969qd9s">
            <button type="button" onClick={back} className="pointer-events-auto group flex items-center gap-2.5 text-mute transition-colors hover:text-enamel" data-id="element-21" data-mp="3iuvteydscera">
              <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-0.5" aria-hidden="true" data-id="element-22" data-mp="2q5ox861t0197" />
              <span className="label-mono text-[10px]" data-id="element-23" data-mp="38rh4iquflax6">Back</span>
            </button>

            <nav className="pointer-events-auto hidden items-center gap-1.5 sm:flex" aria-label="Journey progress" data-id="element-24" data-mp="3dqtmax6g3rsw">
              {sequence.map((step, index) => {
            const visited = index <= stageIndex;
            return <button key={step} type="button" disabled={!visited} onClick={() => goTo(step)} aria-label={stageNames[step] ?? step} aria-current={index === stageIndex ? 'step' : undefined} className="group relative py-3" data-id="element-25" data-mp="3rihz72oiqkax" data-mpi={index}>
                    <span className="block h-px w-8 transition-all duration-500 lg:w-12" style={{
                backgroundColor: index === stageIndex ? accent : visited ? 'rgba(244,242,237,0.35)' : 'rgba(244,242,237,0.12)'
              }} data-id="element-26" data-mp="2266bdxg6yayx" />
                    <span className="label-mono pointer-events-none absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-[8px] text-mute opacity-0 transition-opacity duration-300 group-hover:opacity-100" data-id="element-27" data-mp="ahyoeikxv6nh">
                      {stageNames[step] ?? step}
                    </span>
                  </button>;
          })}
            </nav>

            <button type="button" onClick={reset} className="pointer-events-auto group flex items-center gap-2.5 text-mute transition-colors hover:text-enamel" data-id="element-28" data-mp="t7odqq5xn080">
              <span className="label-mono text-[10px]" data-id="element-29" data-mp="3dwchwztj81ji">Start over</span>
              <RotateCcwIcon className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-rotate-90" aria-hidden="true" data-id="element-30" data-mp="3fdrow7xw6fai" />
            </button>
          </motion.div>}
      </AnimatePresence>

      <SessionMemory open={open} onClose={() => setOpen(false)} data-id="element-31" data-mp="36dbhwohxwq0w" />
    </>;
}